/**
 * Tab 1: Process-Level Risk Ranking
 * 
 * Features:
 * - Quantitative Assessment (rich text area with auto-save)
 * - Qualitative Assessment (dynamic table with CRUD operations)
 * - Fixed rows: Fraud Risk (top) and Overall Risk Ranking Conclusion (bottom)
 * - Hybrid editing: inline risk ranking edit + side panel for full edit
 * - Cross-tab synchronization ready
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../../store';
import { useAssessmentDetails } from '../../../../hooks/useAssessmentDetails';
import {
  createQualitativeFactor,
  updateQualitativeFactor,
  deleteQualitativeFactor,
  updateQuantitativeAssessment,
  updateFraudRisk,
  updateOverallRisk,
  denormalizeRiskRanking,
  getQualitativeFactors,
  parseQualitativeFactors,
  normalizeRiskRanking,
} from '../../../../services/riskAssessmentService';
import { setAssessmentDetails, setManualQualitativeFactors } from '../../../../store/slices/riskAssessmentSlice';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import {
  RichTextEditorComponent,
  Toolbar,
  HtmlEditor,
  Link,
  QuickToolbar,
} from '@syncfusion/ej2-react-richtexteditor';
import EditIcon from '../../../../assets/icons/ic-edit.svg?react';
import TrashIcon from '../../../../assets/icons/ic-trash.svg?react';
import ArrowUpIcon from '../../../../assets/icons/ic-arrow-up.svg?react';
import ArrowDownIcon from '../../../../assets/icons/ic-arrow-down.svg?react';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import QualitativeItemDrawer, { QualitativeFactorFormData } from './QualitativeItemDrawer';
import warningIcon from '../../../../assets/icons/ic-warning.svg';

// ==================== TYPES ====================

// Import QualitativeFactor type from Redux slice
import type { QualitativeFactor } from '../../../../store/slices/riskAssessmentSlice';
import { RiskRanking } from '@/services/types';

// ==================== STYLED COMPONENTS ====================

const TabContent = styled.div`
  min-height: 400px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AssessmentSection = styled.div<{ $bgColor: string }>`
  margin-bottom: 32px;
  background: ${props => props.$bgColor};
  border-radius: 6px;
`;

const AssessmentSectionHeader = styled.div`
  padding: 16px;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AssessmentSectionContent = styled.div`
  padding: 0px 6px 2px 6px;
`;

const RichTextEditorWrapper = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: visible;
  transition: border-color 0.3s;
  
  /* Highlight border when editor is focused */
  &:focus-within {
    border-color: #2563EB;
  }
  
  .e-richtexteditor {
    border: none;
    border-radius: 6px;
    font-family: Inter;
  }
  
  .e-richtexteditor .e-toolbar-wrapper {
    border-bottom: none;
  }
  
  .e-richtexteditor .e-rte-toolbar {
    background: white !important;
    border-bottom: 1px solid #E5E7EB;
    border-radius: 6px 6px 0 0;
    padding: 4px 8px;
    min-height: 44px;
  }
  
  .e-richtexteditor .e-toolbar {
    background: white !important;
    flex-wrap: wrap;
    overflow-x: auto;
    overflow-y: visible;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-items {
    flex-wrap: wrap;
    background: white !important;
  }
  
  .e-richtexteditor .e-toolbar .e-toolbar-item {
    background: transparent !important;
  }
  
  .e-richtexteditor .e-rte-content {
    min-height: 150px;
    font-size: 14px;
    line-height: 20px;
    color: #1F2937;
    border-top: none;
  }
  
  .e-richtexteditor .e-rte-content .e-content {
    padding: 12px;
  }
  
  /* Toolbar items styling */
  .e-toolbar-item {
    margin: 2px;
  }
  
  .e-toolbar-item .e-tbar-btn {
    color: #374151;
    border: none;
    background: transparent;
    padding: 6px 8px;
    min-width: 32px;
    height: 32px;
    border-radius: 4px;
  }
  
  .e-toolbar-item .e-tbar-btn:hover {
    background: #F3F4F6;
    color: #1F2937;
  }
  
  .e-toolbar-item .e-tbar-btn.e-active {
    background: #EFF6FF;
    color: #2563EB;
  }
  
  /* Separator styling */
  .e-toolbar .e-separator {
    border-color: #E5E7EB;
    margin: 0 4px;
  }
  
  /* Dropdown button styling */
  .e-toolbar-item .e-dropdown-btn {
    color: #374151;
    border: none;
    background: transparent !important;
    padding: 6px 8px;
    height: 32px;
    border-radius: 4px;
  }
  
  .e-toolbar-item .e-dropdown-btn:hover {
    background: #F3F4F6 !important;
  }
  
  /* Clear background for all toolbar children */
  .e-richtexteditor .e-toolbar .e-tbar-btn-text,
  .e-richtexteditor .e-toolbar .e-btn-icon,
  .e-richtexteditor .e-toolbar .e-icons,
  .e-richtexteditor .e-toolbar-item .e-rte-dropdown-btn-text {
    background: transparent !important;
  }
  
  /* Hide toolbar extended menu button */
  .e-richtexteditor .e-toolbar .e-toolbar-extended {
    display: none;
  }
  
  /* Hide source code textarea/view */
  .e-richtexteditor .e-rte-srctextarea,
  .e-rte-source-code-textarea,
  .e-source-content {
    display: none !important;
  }
  
  /* Hide inline quick toolbar for text selection, but keep it for links */
  .e-rte-inline-popup {
    display: none !important;
  }
  
  /* Style quick toolbar for links */
  .e-rte-quick-popup .e-rte-toolbar {
    background: white;
    border-radius: 6px;
  }
  
  /* Hide resize handle */
  .e-richtexteditor .e-rte-resize {
    display: none !important;
  }
`;

const GridWrapper = styled.div`
  background: white;
  border-radius: 6px;
  overflow: hidden;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:first-child {
    padding: 6px 4px;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 12px 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .e-grid .e-rowcell:first-child {
    padding: 6px 4px;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span<{ $danger?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: ${props => props.$danger ? '#EF4444' : '#1F2937'};
`;

const CommentCell = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: calc(1.5em * 4);
  word-break: break-word;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// ==================== COMPONENT ====================

const Tab1ProcessLevelRiskRanking: React.FC = () => {
  const { id: assessmentId } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  // Use assessment details hook for refetching details only
  const {
    isLoading: isLoadingDetails,
    refetchDetails,
  } = useAssessmentDetails(assessmentId);

  // Get data from Redux
  const {
    currentAssessmentDetails,
    manualQualitativeFactors,
  } = useSelector((state: RootState) => state.riskAssessment);

  // Local states
  const [quantitativeText, setQuantitativeText] = useState('');
  const [isLoadingQualitatives, setIsLoadingQualitatives] = useState(false);
  
  // Ref to prevent duplicate fetches
  const hasFetchedQualitativesRef = useRef(false);
  const currentAssessmentIdRef = useRef<string | undefined>(undefined);

  /**
   * Build final qualitative factors list:
   * Fraud Risk (from details) + Manual factors (from API) + Overall Conclusion (from details)
   */
  const qualitativeFactors = useMemo(() => {
    if (!currentAssessmentDetails) {
      return [];
    }

    return [
      // Fixed row 1: Fraud Risk
      {
        id: 'qf-fraud-risk',
        qualitativeFactor: 'Fraud Risk',
        riskRanking: normalizeRiskRanking(currentAssessmentDetails.fraudRiskRanking),
        customComment: currentAssessmentDetails.fraudRiskDescription || '',
        isFixed: true,
        displayOrder: 0,
      },
      // Manual qualitative factors from API
      ...manualQualitativeFactors.map((qf, index) => ({
        ...qf,
        isFixed: false,
        displayOrder: index + 1,
      })),
      // Fixed row 2: Overall Risk Ranking Conclusion
      {
        id: 'qf-overall-conclusion',
        qualitativeFactor: 'Overall Risk Ranking Conclusion',
        riskRanking: normalizeRiskRanking(currentAssessmentDetails.overallRiskRanking),
        customComment: currentAssessmentDetails.overallRiskDescription || '',
        isFixed: true,
        displayOrder: manualQualitativeFactors.length + 1,
      },
    ];
  }, [currentAssessmentDetails, manualQualitativeFactors, selectedReport?.annualReportId]);

  /**
   * Fetch qualitative factors from API
   */
  const fetchQualitativeFactors = useCallback(async (silent = false) => {
    if (!assessmentId) {
      console.warn('⚠️ Cannot fetch qualitative factors: assessmentId is undefined');
      return;
    }

    if (!silent) {
      setIsLoadingQualitatives(true);
    }

    try {
      // console.log('🔄 Fetching qualitative factors for:', assessmentId);
      const response = await getQualitativeFactors(assessmentId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });

      const parsed = parseQualitativeFactors(response.result);
      dispatch(setManualQualitativeFactors(parsed));
      // console.log(`✅ Loaded ${parsed.length} manual qualitative factors`);
    } catch (err: any) {
      console.error('❌ Failed to fetch qualitative factors:', err);
      if (!silent) {
        message.error(err?.message || 'Failed to load qualitative factors');
      }
    } finally {
      if (!silent) {
        setIsLoadingQualitatives(false);
      }
    }
  }, [assessmentId, dispatch, selectedReport?.annualReportId]);

  /**
   * Fetch qualitative factors when assessmentId or currentAssessmentDetails changes
   */
  useEffect(() => {
    // Wait for currentAssessmentDetails to be available
    if (!assessmentId || !currentAssessmentDetails) {
      return;
    }

    // Check if we need to fetch (new assessmentId or first time)
    const isNewAssessment = currentAssessmentIdRef.current !== assessmentId;
    
    if (isNewAssessment) {
      // console.log(`🆕 Qualitatives: Assessment ID changed: ${currentAssessmentIdRef.current} → ${assessmentId}`);
      hasFetchedQualitativesRef.current = false;
      currentAssessmentIdRef.current = assessmentId;
    }

    // Skip if already fetched for this assessment
    if (hasFetchedQualitativesRef.current) {
      // console.log('⏭️ Skipping duplicate qualitatives fetch for:', assessmentId);
      return;
    }

    // Mark as fetched before fetching to prevent duplicates
    hasFetchedQualitativesRef.current = true;

    // Fetch qualitative factors
    fetchQualitativeFactors();
  }, [assessmentId, currentAssessmentDetails, fetchQualitativeFactors]);

  // Sync quantitative text from Redux when it loads
  useEffect(() => {
    if (currentAssessmentDetails?.quantitativeAssessment) {
      setQuantitativeText(currentAssessmentDetails.quantitativeAssessment);
    }
  }, [currentAssessmentDetails?.quantitativeAssessment, selectedReport?.annualReportId]);

  // Drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedFactorForEdit, setSelectedFactorForEdit] = useState<QualitativeFactor | null>(null);
  const [insertFactorAtIndex, setInsertFactorAtIndex] = useState<number | null>(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [factorToDelete, setFactorToDelete] = useState<QualitativeFactor | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleQuantitativeChange = async (args: any) => {
    if (!currentAssessmentDetails) {
      return;
    }
    const newQuantitativeText = args.value;
    setQuantitativeText(newQuantitativeText);
    await updateQuantitativeAssessment({
      assessmentId: currentAssessmentDetails.assessmentId,
      content: newQuantitativeText,
    });
    await refetchDetails(true);
  };

  // Ref for quantitative RTE
  const quantitativeRteRef = useRef<any>(null);

  // Enable spellcheck after RTE is created
  const handleQuantitativeRTECreated = useCallback(() => {
    if (quantitativeRteRef.current) {
      try {
        // For div mode (contenteditable)
        const editableElement = quantitativeRteRef.current.contentModule?.getEditPanel?.();
        if (editableElement) {
          editableElement.setAttribute('spellcheck', 'true');
        }
        // For iframe mode
        const iframeDoc = quantitativeRteRef.current.contentModule?.getDocument?.();
        if (iframeDoc?.body) {
          iframeDoc.body.setAttribute('spellcheck', 'true');
        }
      } catch (error) {
        console.warn('Could not enable spellcheck on RTE', error);
      }
    }
  }, []);

  const handleAddFactorAbove = (factor: QualitativeFactor) => {
    const factorIndex = qualitativeFactors.findIndex(f => f.id === factor.id);
    setInsertFactorAtIndex(factorIndex);
    setDrawerMode('add');
    setSelectedFactorForEdit(null);
    setIsDrawerOpen(true);
  };

  const handleAddFactorBelow = (factor: QualitativeFactor) => {
    const factorIndex = qualitativeFactors.findIndex(f => f.id === factor.id);
    setInsertFactorAtIndex(factorIndex + 1);
    setDrawerMode('add');
    setSelectedFactorForEdit(null);
    setIsDrawerOpen(true);
  };

  const handleEditFactor = (factor: QualitativeFactor) => {
    setDrawerMode('edit');
    setSelectedFactorForEdit(factor);
    setIsDrawerOpen(true);

    // Special handling for fixed rows
    if (factor.id === 'qf-fraud-risk') {
      // console.log('Editing Fraud Risk - will sync with Tab 3');
    } else if (factor.id === 'qf-overall-conclusion') {
      // console.log('Editing Overall Risk Ranking Conclusion - will update List View');
    }
  };

  const handleDeleteFactor = (factor: QualitativeFactor) => {
    setFactorToDelete(factor);
    setShowDeleteModal(true);
  };

  const handleDeleteFactorConfirm = async () => {
    if (!factorToDelete) {
      message.error('Cannot delete factor');
      return;
    }

    setIsDeleting(true);
    try {
      // console.log('📤 Deleting qualitative factor...');
      await deleteQualitativeFactor(factorToDelete.id);

      // Refetch only qualitative factors (silent - no loading)
      await fetchQualitativeFactors(true);

      message.success('Qualitative factor deleted successfully');

      setShowDeleteModal(false);
      setFactorToDelete(null);

    } catch (error: any) {
      console.error('❌ Failed to delete factor:', error);
      message.error(error?.message || 'Failed to delete factor');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteFactorCancel = () => {
    setShowDeleteModal(false);
    setFactorToDelete(null);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedFactorForEdit(null);
    setInsertFactorAtIndex(null);
  };

  const handleDrawerSubmit = async (formData: QualitativeFactorFormData) => {
    if (!currentAssessmentDetails) {
      return;
    }
    try {
      if (drawerMode === 'add') {
        // console.log('📤 Creating qualitative factor...');
        
        // Calculate correct insert index (exclude fixed rows)
        const targetIndex = insertFactorAtIndex !== null 
          ? Math.max(0, insertFactorAtIndex - 1) // Subtract 1 to account for Fraud Risk row
          : manualQualitativeFactors.length;
        
        await createQualitativeFactor({
          assessmentId: currentAssessmentDetails.assessmentId,
          index: targetIndex,
          qualitativeFactor: formData.qualitativeFactor,
          riskRating: denormalizeRiskRanking(formData.riskRanking),
          comment: formData.customComment,
        });

        // Refetch only qualitative factors (silent - no loading)
        await fetchQualitativeFactors(true);
        message.success('Qualitative factor added successfully');
      } else {
        if (!selectedFactorForEdit) {
          return;
        }

        if (selectedFactorForEdit.id === 'qf-fraud-risk') {
          // console.log('🔄 Fraud Risk updated - syncing with Tab 3 via Redux');
          // console.log('📤 Updating fraud risk...');
          await updateFraudRisk({
            assessmentId: currentAssessmentDetails.assessmentId,
            riskRanking: denormalizeRiskRanking(formData.riskRanking),
            description: formData.customComment,
          });
          // Refetch details to update Redux
          await refetchDetails(true);
          // console.log('✅ Fraud risk updated successfully');
        } else if (selectedFactorForEdit?.id === 'qf-overall-conclusion') {
          // console.log('🔄 Overall Risk Ranking Conclusion updated - syncing with List View');
          // console.log('📤 Updating overall risk...');
          await updateOverallRisk({
            assessmentId: currentAssessmentDetails.assessmentId,
            riskRanking: denormalizeRiskRanking(formData.riskRanking),
            description: formData.customComment,
          });
          // Refetch details to update Redux
          await refetchDetails(true);
          // console.log('✅ Overall risk updated successfully');
        } else {
          // console.log('📤 Updating qualitative factor...');
          // Find index in manual factors (exclude fixed rows)
          const factorIndex = manualQualitativeFactors.findIndex(f => f.id === selectedFactorForEdit!.id);
          await updateQualitativeFactor({
            assessmentId: currentAssessmentDetails.assessmentId,
            index: factorIndex,
            qualitativeFactor: formData.qualitativeFactor,
            riskRating: denormalizeRiskRanking(formData.riskRanking),
            comment: formData.customComment,
            assessmentItemId: selectedFactorForEdit!.id, // Backend naming
          });
          // Refetch only qualitative factors (silent - no loading)
          await fetchQualitativeFactors(true);
        }
        message.success('Qualitative factor updated successfully');
      }
    } catch (error: any) {
      console.error('❌ Failed to save qualitative factor:', error);
      const defaultErrorMessage = `Failed to ${drawerMode === 'add' ? 'add' : 'update'} factor`;
      message.error(error?.message || defaultErrorMessage);
      throw error;
    }
  };

  // Handle inline risk ranking change
  const handleRiskRankingChange = async (factorId: string, newRanking: RiskRanking) => {
    if (!currentAssessmentDetails) {
      return;
    }
    try {
      // Sync logic for fixed rows (inline edit)
      if (factorId === 'qf-fraud-risk') {
        const factor = qualitativeFactors.find(f => f.id === factorId);
        if (factor) {
          // Optimistic update to Redux
          dispatch(setAssessmentDetails({
            ...currentAssessmentDetails,
            fraudRiskRanking: newRanking,
          }));
          // console.log('📤 Updating fraud risk ranking (inline)...');
          await updateFraudRisk({
            assessmentId: currentAssessmentDetails.assessmentId,
            riskRanking: denormalizeRiskRanking(newRanking),
            description: factor.customComment,
          });
          // Refetch details to update Redux
          await refetchDetails(true);
          // console.log('✅ Fraud risk ranking updated successfully');
        }
      } else if (factorId === 'qf-overall-conclusion') {
        // console.log('🔄 Overall Risk Ranking Conclusion changed (inline) - syncing with List View:', newRanking);
        const factor = qualitativeFactors.find(f => f.id === factorId);
        if (factor) {
          // Optimistic update to Redux
          dispatch(setAssessmentDetails({
            ...currentAssessmentDetails,
            overallRiskRanking: newRanking,
          }));
          // console.log('📤 Updating overall risk ranking (inline)...');
          await updateOverallRisk({
            assessmentId: currentAssessmentDetails.assessmentId,
            riskRanking: denormalizeRiskRanking(newRanking),
            description: factor.customComment,
          });
          // Refetch details to update Redux
          await refetchDetails(true);
          // console.log('✅ Overall risk ranking updated successfully');
        }
      } else {
        // Regular qualitative factor - call API to update
        const factor = manualQualitativeFactors.find(f => f.id === factorId);
        if (factor) {
          // console.log('📤 Updating qualitative factor ranking (inline)...');
          const factorIndex = manualQualitativeFactors.findIndex(f => f.id === factorId);
          await updateQualitativeFactor({
            assessmentId: currentAssessmentDetails.assessmentId,
            index: factorIndex,
            qualitativeFactor: factor.qualitativeFactor,
            riskRating: denormalizeRiskRanking(newRanking),
            comment: factor.customComment,
            assessmentItemId: factorId,
          });
          // Refetch only qualitative factors (silent - no loading)
          await fetchQualitativeFactors(true);
          // console.log('✅ Risk ranking updated successfully');
        }
      }
    } catch (error: any) {
      console.error('❌ Failed to update risk ranking:', error);
      message.error(error?.message || 'Failed to update risk ranking');
    }
  };

  // Action template
  const actionTemplate = (props: any) => {
    const isFixedRow = props.isFixed;
    const factorIndex = qualitativeFactors.findIndex(f => f.id === props.id);
    const isLastRow = factorIndex === qualitativeFactors.length - 1;
    const isFirstRow = factorIndex === 0;
    const getMenuItems = (): MenuProps['items'] => [
      {
        key: 'add-above',
        label: (
          <MenuItemContent style={{ opacity: isFirstRow ? 0.7 : 1 }}>
            <ArrowUpIcon />
            <MenuItemText>Add row above</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleAddFactorAbove(props),
        disabled: isFirstRow,
      },
      {
        key: 'add-below',
        label: (
          <MenuItemContent style={{ opacity: isLastRow ? 0.7 : 1 }}>
            <ArrowDownIcon />
            <MenuItemText>Add row below</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleAddFactorBelow(props),
        disabled: isLastRow,
      },
      {
        key: 'edit',
        label: (
          <MenuItemContent>
            <EditIcon />
            <MenuItemText>Edit Item</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleEditFactor(props),
      },
      {
        key: 'delete',
        label: (
          <MenuItemContent style={{ opacity: isFixedRow ? 0.7 : 1 }}>
            <TrashIcon />
            <MenuItemText $danger>Delete row</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => handleDeleteFactor(props),
        disabled: isFixedRow,
      },
    ];

    const handleActionButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <div onClick={handleActionButtonClick}>
        <Dropdown
          menu={{ items: getMenuItems() }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      </div>
    );
  };

  // Comment template - shows up to 4 lines
  const customCommentTemplate = (props: any) => {
    return (
      <CommentCell title={props.customComment}>
        {props.customComment}
      </CommentCell>
    );
  };

  // Risk ranking template
  const riskRankingTemplate = (props: any) => {
    const getRiskMenuItems = (): MenuProps['items'] => [
      { key: 'High', label: 'High', onClick: () => handleRiskRankingChange(props.id, 'High' as RiskRanking) },
      { key: 'Med', label: 'Med', onClick: () => handleRiskRankingChange(props.id, 'Med' as RiskRanking) },
      { key: 'Low', label: 'Low', onClick: () => handleRiskRankingChange(props.id, 'Low' as RiskRanking) },
      { key: 'Unranked', label: 'Unranked', onClick: () => handleRiskRankingChange(props.id, 'Unranked' as RiskRanking) },
    ];

    if (!isAdmin) {
      return (
        <RiskChip $risk={props.riskRanking} style={{ cursor: 'default' }}>
          {props.riskRanking}
        </RiskChip>
      );
    }

    return (
      <Dropdown
        menu={{ items: getRiskMenuItems() }}
        trigger={['click']}
        placement="bottomRight"
      >
        <RiskChip $risk={props.riskRanking}>
          {props.riskRanking}
        </RiskChip>
      </Dropdown>
    );
  };

  // Show loading state
  if (isLoadingDetails || isLoadingQualitatives) {
    return (
      <TabContent>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: '#6B7280',
          fontSize: '14px'
        }}>
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        </div>
      </TabContent>
    );
  }

  return (
    <>
      <TabContent>
        {/* Quantitative Assessment Section */}
        <AssessmentSection $bgColor="#E0E7FF">
          <AssessmentSectionHeader>Quantitative Assessment</AssessmentSectionHeader>
          <AssessmentSectionContent>
            <RichTextEditorWrapper>
              <RichTextEditorComponent
                ref={quantitativeRteRef}
                value={quantitativeText}
                change={handleQuantitativeChange}
                created={handleQuantitativeRTECreated}
                readonly={!isAdmin}
                toolbarSettings={{
                  enable: true,
                  enableFloating: false,
                  items: [
                    'Bold', 'Italic', 'Underline', 'StrikeThrough', 'InlineCode', '|',
                    'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
                    'Formats', 'Alignments', '|',
                    'BulletFormatList', 'NumberFormatList', 'Checklist', '|',
                    'CreateLink', 'HorizontalLine', 'Blockquote', '|',
                    'LowerCase', 'UpperCase', '|',
                    'Undo', 'Redo'
                  ]
                }}
                quickToolbarSettings={{
                  link: ['Open', 'Edit', 'UnLink']
                }}
                format={{
                  width: '100px'
                }}
              >
                <Inject services={[Toolbar, HtmlEditor, Link, QuickToolbar]} />
              </RichTextEditorComponent>
            </RichTextEditorWrapper>
          </AssessmentSectionContent>
        </AssessmentSection>

        {/* Qualitative Assessment Section */}
        <AssessmentSection $bgColor="#EDE9FE">
          <AssessmentSectionHeader>
            <span>Qualitative Assessment</span>
          </AssessmentSectionHeader>
          <AssessmentSectionContent>
            <GridWrapper>
              <GridComponent
                key={`grid-${qualitativeFactors.length}`}
                dataSource={qualitativeFactors}
                allowPaging={false}
                enableHover={true}
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field='qualitativeFactor'
                    headerText='Qualitative Factors'
                    width='300'
                    textAlign='Left'
                  />
                  <ColumnDirective
                    field='riskRanking'
                    headerText='Risk Rankings'
                    width='140'
                    textAlign='Left'
                    template={riskRankingTemplate}
                  />
                  <ColumnDirective
                    field='customComment'
                    headerText='Comments regarding qualitative assessment risk ranking'
                    width='auto'
                    textAlign='Left'
                    template={customCommentTemplate}
                  />
                  <ColumnDirective
                    headerText='Action'
                    width='80'
                    textAlign='Center'
                    template={actionTemplate}
                    visible={isAdmin}
                  />
                </ColumnsDirective>
                <Inject services={[Page]} />
              </GridComponent>
            </GridWrapper>
          </AssessmentSectionContent>
        </AssessmentSection>
      </TabContent>

      {/* Qualitative Factor Drawer */}
      <QualitativeItemDrawer
        open={isDrawerOpen}
        mode={drawerMode}
        initialValues={selectedFactorForEdit ? {
          id: selectedFactorForEdit.id,
          qualitativeFactor: selectedFactorForEdit.qualitativeFactor,
          riskRanking: selectedFactorForEdit.riskRanking,
          customComment: selectedFactorForEdit.customComment,
          isFixed: selectedFactorForEdit.isFixed,
        } : undefined}
        onClose={handleDrawerClose}
        onSubmit={handleDrawerSubmit}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={handleDeleteFactorCancel}
        onConfirm={handleDeleteFactorConfirm}
        icon={warningIcon}
        title="Delete Qualitative Factor"
        description={
          factorToDelete ? (
            <div style={{ textAlign: 'left' }}>
              Are you sure you want to delete <strong>{factorToDelete.qualitativeFactor}</strong>?
              <div style={{ color: '#DC2626', fontWeight: 600, marginTop: '12px' }}>
                This action cannot be undone.
              </div>
            </div>
          ) : 'Are you sure you want to delete this qualitative factor?'
        }
        confirmText="Delete Factor"
        cancelText="Cancel"
        danger={true}
        loading={isDeleting}
      />

    </>
  );
};

export default Tab1ProcessLevelRiskRanking;

